import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle } from 'lucide-react';
import { cleanEmails, findImplausibleEmails, getInfo } from '../../utils/api';
import { toast } from 'sonner';
import { useT } from '../../i18n';
import MissingChoice from './MissingChoice';
import InvalidEmailPanel from './InvalidEmailPanel';
import DuplicateEmailPanel from './DuplicateEmailPanel';

// Chaque clé est passée en toutes lettres à la fonction de traduction.
// Composée à la volée — depuis une
// table ou un gabarit — elle serait invisible au garde-fou qui traque les
// traductions mortes, et une faute de frappe afficherait le code brut sans que
// rien ne le signale.
const reasonText = (t, reason) => {
  switch (reason) {
    case 'reserved_domain': return t('email.reason.reserved_domain');
    case 'repeated_character_local': return t('email.reason.repeated_character_local');
    case 'repeated_character_domain': return t('email.reason.repeated_character_domain');
    case 'keyboard_run': return t('email.reason.keyboard_run');
    case 'throwaway_word': return t('email.reason.throwaway_word');
    case 'local_equals_domain': return t('email.reason.local_equals_domain');
    default: return reason;
  }
};

const EmailCleanOperation = ({ onSuccess, onCancel }) => {
  const t = useT();
  const [column, setColumn] = useState('');
  const [columns, setColumns] = useState([]);
  const [normalize, setNormalize] = useState(true);
  const [validate, setValidate] = useState(false);
  const [removeInvalid, setRemoveInvalid] = useState(false);
  const [extractDomain, setExtractDomain] = useState(false);
  const [removeDuplicates, setRemoveDuplicates] = useState(false);
  const [loading, setLoading] = useState(false);
  // Ce que l'aperçu a trouvé, et ce qui est coché. Les « certaines » le sont
  // d'office ; les « à vérifier » ne le sont jamais — mesuré, une règle seule
  // signalait quatre vraies adresses sur huit.
  const [junk, setJunk] = useState(null);
  const [ticked, setTicked] = useState([]);
  const [searching, setSearching] = useState(false);

  const lookForJunk = async () => {
    setSearching(true);
    try {
      const result = await findImplausibleEmails({ column });
      setJunk(result.report);
      setTicked(result.report.addresses
        .filter((entry) => entry.level === 'certain')
        .map((entry) => entry.value));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('email.failed'));
      setJunk(null);
    } finally {
      setSearching(false);
    }
  };

  const toggle = (value) => setTicked((current) =>
    current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value]);

  // Ce qui est à l'écran décrit toujours la colonne à l'écran.
  useEffect(() => { setJunk(null); setTicked([]); }, [column]);

  const tickedRows = (junk?.addresses || [])
    .filter((entry) => ticked.includes(entry.value))
    .reduce((total, entry) => total + entry.rows, 0);

  useEffect(() => {
    const loadColumns = async () => {
      try {
        const result = await getInfo();
        const cols = result.info.column_names || [];
        setColumns(cols);
        
        // Auto-select a column whose name says it holds addresses. When none
        // does, leave the field empty rather than falling back to the first
        // column: that pre-aimed the operation at whatever came first — an id,
        // a phone number.
        //
        // Le champ vide ne coûte alors qu'un clic *parce que* `MissingChoice`
        // le réclame : sur un fichier dont la colonne s'appelle autrement, les
        // trois contrôles éteints et muets se lisaient comme une panne.
        const emailCol = cols.find(col =>
          col.toLowerCase().includes('email') ||
          col.toLowerCase().includes('mail') ||
          col.toLowerCase().includes('courriel')
        );

        if (emailCol) {
          setColumn(emailCol);
        }
      } catch (error) {
        toast.error(t('form.loadColumnsFailed'));
      }
    };
    loadColumns();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const config = {
        column,
        normalize,
        validate,
        remove_invalid: validate && removeInvalid,
        extract_domain: extractDomain,
        remove_duplicates: removeDuplicates,
        remove_values: ticked
      };
      
      const result = await cleanEmails(config);
      onSuccess(result);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('email.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="label mb-1">{t('email.column')}</label>
        <select 
          value={column} 
          onChange={(e) => setColumn(e.target.value)} 
          className="select"
          required
        >
          <option value="">{t('email.selectColumn')}</option>
          {columns.map(col => (
            <option key={col} value={col}>{col}</option>
          ))}
        </select>
      </div>

      <div className="card p-4 space-y-3">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Mail className="h-4 w-4" />
          {t('email.optionsTitle')}
        </h3>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={normalize}
            onChange={(e) => setNormalize(e.target.checked)}
            className="h-4 w-4 mt-0.5"
          />
          <div className="flex-1">
            <span className="text-sm font-medium">{t('email.normalize')}</span>
            <p className="text-xs text-muted-foreground">{t('email.normalizeHelp')}</p>
          </div>
        </label>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={validate}
            onChange={(e) => setValidate(e.target.checked)}
            className="h-4 w-4 mt-0.5"
          />
          <div className="flex-1">
            <span className="text-sm font-medium">{t('email.validate')}</span>
            <p className="text-xs text-muted-foreground">{t('email.validateHelp')}</p>
          </div>
        </label>

        {validate && (
          <div className="ml-7">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={removeInvalid}
                onChange={(e) => setRemoveInvalid(e.target.checked)}
                className="h-4 w-4 mt-0.5"
              />
              <div className="flex-1">
                <span className="text-sm font-medium">{t('email.removeInvalid')}</span>
                <p className="text-xs text-muted-foreground">{t('email.removeInvalidHelp')}</p>
              </div>
            </label>
          </div>
        )}

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={extractDomain}
            onChange={(e) => setExtractDomain(e.target.checked)}
            className="h-4 w-4 mt-0.5"
          />
          <div className="flex-1">
            <span className="text-sm font-medium">{t('email.extractDomain')}</span>
            <p className="text-xs text-muted-foreground">{t('email.extractDomainHelp')}</p>
          </div>
        </label>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={removeDuplicates}
            onChange={(e) => setRemoveDuplicates(e.target.checked)}
            className="h-4 w-4 mt-0.5"
          />
          <div className="flex-1">
            <span className="text-sm font-medium">{t('email.removeDuplicates')}</span>
            <p className="text-xs text-muted-foreground">{t('email.removeDuplicatesHelp')}</p>
          </div>
        </label>
      </div>

      <div className="card p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-2">
          <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5" />
          <div className="text-xs text-blue-800">
            <strong>{t('email.exampleTitle')}</strong>
            <br />{t('email.exampleBefore', { value: 'John.Doe@GMAIL.COM' })}
            <br />{t('email.exampleAfter', { value: 'john.doe@gmail.com' })}
            {extractDomain && <><br />{t('email.exampleDomain', { value: 'gmail.com' })}</>}
          </div>
        </div>
      </div>

      <div className="card p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium">{t('email.findJunk')}</p>
            <p className="text-xs text-muted-foreground">{t('email.findJunkHelp')}</p>
          </div>
          <button
            type="button"
            onClick={lookForJunk}
            disabled={!column || searching}
            className="btn-secondary whitespace-nowrap"
            data-testid="email-find-junk"
          >
            {t('email.findJunk')}
          </button>
        </div>

        {junk && junk.addresses.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="email-junk-none">
            {t('email.junkNone')}
          </p>
        )}

        {junk && junk.addresses.length > 0 && (
          <div className="space-y-3" data-testid="email-junk-plan">
            {['certain', 'review'].map((level) => {
              const rows = junk.addresses.filter((entry) => entry.level === level);
              if (rows.length === 0) return null;
              return (
                <div key={level}>
                  <p className="text-xs font-medium text-muted-foreground mb-1">
                    {level === 'certain' ? t('email.junkCertain') : t('email.junkReview')}
                  </p>
                  <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
                    {rows.map((entry) => (
                      <label key={entry.value} className="flex items-start gap-2 text-sm">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={ticked.includes(entry.value)}
                          onChange={() => toggle(entry.value)}
                        />
                        <span className="flex-1 min-w-0">
                          <span className="font-medium break-all">{entry.value}</span>
                          <span className="text-xs text-muted-foreground ml-2">
                            {t('email.junkRows', { count: entry.rows })}
                          </span>
                          <span className="block text-xs text-muted-foreground">
                            {entry.reasons.map((reason) => reasonText(t, reason)).join(' · ')}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}

            {junk.truncated && (
              <p className="text-xs text-muted-foreground">{t('email.junkMore')}</p>
            )}
            <p className="text-sm font-medium" data-testid="email-junk-selected">
              {t('email.junkSelected', { count: ticked.length, rows: tickedRows })}
            </p>
          </div>
        )}
      </div>

      <InvalidEmailPanel column={column} normalize={normalize} />

      <DuplicateEmailPanel
        column={column}
        normalize={normalize}
        removeInvalid={validate && removeInvalid}
        removeValues={ticked}
      />

      <MissingChoice reasons={[!column && t('email.needColumn')]} />

      <div className="flex gap-2 pt-4 border-t">
        <button 
          type="submit" 
          disabled={loading || !column} 
          className="btn-primary flex-1"
          data-testid="apply-email-clean"
        >
          {loading ? t('email.cleaning') : t('email.apply')}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">
          {t('form.cancel')}
        </button>
      </div>
    </form>
  );
};

export default EmailCleanOperation;
